import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Youtube } from "lucide-react";
import { YOUTUBE_VIDEO_ID, YOUTUBE_IS_SHORT, YOUTUBE_CHANNEL } from "@/data/site";

const VideoCtx = createContext({ openVideo: () => {} });
export const useVideo = () => useContext(VideoCtx);

export function VideoProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [videoId, setVideoId] = useState(YOUTUBE_VIDEO_ID);
  const frameRef = useRef(null);
  // The film is on-demand only: it opens exclusively from the "Watch the film" button
  // (nav + hero) and never auto-plays on site entry.

  // The film plays ONCE. YouTube Shorts players replay endlessly by default, so we listen
  // for the player's "ended" state via the IFrame API, stop the video and dismiss the modal.
  // Shorts players also emit a transient "ended" (0) during initialization — so we only
  // honor it after the player has reported an actual "playing" (1) state.
  const playedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      playedRef.current = false;
      return;
    }
    const onMessage = (e) => {
      let d;
      try {
        d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (!d || d.event !== "onStateChange") return;
      if (d.info === 1) {
        playedRef.current = true;
      } else if (d.info === 0 && playedRef.current) {
        try {
          frameRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: "command", func: "stopVideo", args: [] }),
            "*",
          );
        } catch {
          /* player already gone */
        }
        setOpen(false);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open]);

  const handleOpenChange = (o) => {
    setOpen(o);
    if (!o) {
      window.dispatchEvent(new CustomEvent("exoplusbio:film-closed", { detail: { wasAuto: false } }));
    }
  };

  const openVideo = (vid) => {
    if (typeof vid === "string" && vid) setVideoId(vid);
    setOpen(true);
  };

  return (
    <VideoCtx.Provider value={{ openVideo }}>
      {children}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={`border-none bg-[#070b0c] p-2 sm:p-3 ${YOUTUBE_IS_SHORT ? "max-w-[340px]" : "max-w-3xl"}`}
          aria-describedby={undefined}
          data-testid="video-modal"
        >
          <button
            onClick={() => handleOpenChange(false)}
            className="absolute -top-4 right-2 z-10 inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-4 text-xs font-semibold text-[#070b0c] shadow-lg ring-1 ring-black/10 transition-transform hover:scale-105 sm:-right-4"
            data-testid="video-close-button"
            aria-label="Close film"
          >
            <X className="h-4 w-4" /> Close
          </button>
          <DialogTitle className="sr-only">Exoplus Bio product film</DialogTitle>
          {videoId ? (
            <div
              className={`mx-auto w-full overflow-hidden ${YOUTUBE_IS_SHORT ? "aspect-[9/16] max-h-[78vh]" : "aspect-video"}`}
              data-testid="video-frame"
            >
              <iframe
                ref={frameRef}
                className="h-full w-full"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&enablejsapi=1&loop=0`}
                title="Exoplus Bio product film"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-4 bg-[#0a1112] p-8 text-center">
              <Youtube className="h-10 w-10 text-[#e4f4f1]" data-testid="video-placeholder-icon" />
              <p className="font-display text-xl text-[#f2f5f4]">Our product film is being prepared</p>
              <p className="max-w-sm text-sm text-white/60">
                It will premiere here and on our YouTube channel. Subscribe to be the first to watch.
              </p>
              <a
                href={YOUTUBE_CHANNEL}
                target="_blank"
                rel="noreferrer"
                data-testid="video-subscribe-button"
                className="inline-flex items-center gap-2 bg-white px-5 py-2.5 text-sm font-medium text-[#070b0c] transition-colors hover:bg-[#0e8c8c] hover:text-white"
              >
                Follow on YouTube
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </VideoCtx.Provider>
  );
}

export default VideoProvider;
